import { useEffect, useState } from 'react';

/** 将远程 SVG 的二进制响应转为图片 URL；签名参数保持原样，普通图片直接使用原地址。 */
export async function resolveSvgImageUrl(url: string, signal?: AbortSignal): Promise<string> {
  if (!/^https?:\/\//i.test(url) || !/\.svg$/i.test(url.split(/[?#]/)[0])) return url;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`SVG image request failed: ${response.status}`);
  const blob = new Blob([await response.arrayBuffer()], { type: 'image/svg+xml' });
  return URL.createObjectURL(blob);
}

/** 批量解析图片预览地址，换图或卸载时取消请求并释放 SVG Blob URL。 */
export function useSvgImageUrls(urls: string[]) {
  const [resolved, setResolved] = useState<Record<string, string>>({});
  // 上传进度会更新列表对象；仅地址变化时重新请求，避免重复下载。
  const urlsKey = JSON.stringify([...new Set(urls)]);

  useEffect(() => {
    const controller = new AbortController();
    const objectUrls: string[] = [];
    setResolved({});

    const imageUrls: string[] = JSON.parse(urlsKey);
    imageUrls.forEach(async (url) => {
      try {
        const previewUrl = await resolveSvgImageUrl(url, controller.signal);
        if (controller.signal.aborted) {
          if (previewUrl !== url) URL.revokeObjectURL(previewUrl);
          return;
        }
        if (previewUrl !== url) {
          objectUrls.push(previewUrl);
          setResolved((prev) => ({ ...prev, [url]: previewUrl }));
        }
      } catch {
        // 跨域或网络失败时保留原地址，兼容服务端已正确返回图片类型的情况。
      }
    });

    return () => {
      controller.abort();
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [urlsKey]);

  return resolved;
}
